import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle,
  Crown,
  Zap,
  Check,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { useSubscriptionPlans, useCreateUserAndSubscribe, useUserSubscriptionStatus } from '@/hooks/useSubscriptions';
import { useBillingUsage } from '@/hooks/useBilling';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { getLegalUrls } from '@/lib/legal-urls';
import type { SubscriptionPlan } from '@/types/billing';
// Promo codes ride the ?promo= URL param and apply as Stripe promotion codes
// at checkout (see referral-signup); this is independent of any affiliate
// program.
const getPromoCodeFromUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('promo');
  } catch (_e) {
    return null;
  }
};
import { getTrialCreditCap } from '@/helpers/plans';
import { toast } from 'sonner';

// Registration form schema
const signupSchema = z.object({
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  // SMS consent is OPTIONAL per CTIA/TCPA: consent cannot be a required
  // condition for service or transaction completion. Users can sign up
  // without checking this box and use ScheduleHero AI normally; they
  // simply won't receive SMS until they opt in.
  smsConsent: z.boolean().default(false),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignupFormData = z.infer<typeof signupSchema>;

export default function SignupWithPlansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [step, setStep] = useState<'select-plan' | 'register' | 'success'>('select-plan');
  const [apiError, setApiError] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  // Display-only: Stripe charges the full plan price, so any nonzero value
  // here advertises a discount that is never actually applied.
  const discountPercent = 0;
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Authentication and subscription status hooks. Billing/status queries
  // require a session; leaving them enabled for anonymous visitors made the
  // whole page sit on the loading spinner while they failed and retried.
  const { user, loading: authLoading } = useAuth();
  const { data: billingUsage, isLoading: billingLoading } = useBillingUsage({ enabled: !!user });
  const { data: subscriptionStatus, isLoading: statusLoading } = useUserSubscriptionStatus({ enabled: !!user });
  const { branding, isLoading: brandingLoading } = useBranding();
  const legalUrls = getLegalUrls(branding);
  
  const { data: plans, isLoading: plansLoading, error: plansError } = useSubscriptionPlans();
  const createUserAndSubscribe = useCreateUserAndSubscribe();

  // Clean up redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // Pick up a promo code from the URL
  useEffect(() => {
    const promo = getPromoCodeFromUrl();
    if (promo) {
      setPromoCode(promo);
    }
  }, [searchParams]);

  // Check for logged-in users with existing plans and redirect them
  useEffect(() => {
    // Wait for auth and billing data to load
    if (authLoading || billingLoading || statusLoading) return;

    // If user is logged in and has an active plan, redirect to dashboard
    if (user && (billingUsage?.hasSubscriptionPlan || subscriptionStatus?.has_plan_assignment)) {
      toast.info('You already have an active plan! Redirecting to dashboard.', {
        duration: 3000,
      });
      
      navigate('/dashboard');
    }
  }, [
    user,
    billingUsage,
    subscriptionStatus,
    authLoading,
    billingLoading,
    statusLoading,
    navigate
  ]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { smsConsent: false },
  });

  // Exclude retired referrals_only plans (kept active in the DB for their
  // existing holders; referral-signup rejects new purchases of them too).
  // Sort by price, not display_order: production data repurposed
  // display_order to mean "badge goes here" (Pro=1), not visual order.
  const visiblePlans = (plans?.filter((plan: SubscriptionPlan) => plan.is_active && !plan.referrals_only) || [])
    .sort((a, b) => a.price_monthly - b.price_monthly || a.price_one_time - b.price_one_time);

  // Highlight the middle plan when there are 3+, else the last one
  // (matches the marketing homepage's pricing section).
  const highlightIdx = visiblePlans.length >= 3 ? Math.floor(visiblePlans.length / 2) : visiblePlans.length - 1;

  // Calculate discounted prices
  const calculateDiscountedPrice = (originalPrice: number) => {
    if (discountPercent === 0) return originalPrice;
    return Math.round(originalPrice * (100 - discountPercent)) / 100;
  };


  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setStep('register');
  };

  const onSubmit = async (formData: SignupFormData) => {
    if (!selectedPlan) return;

    setApiError(null);

    try {
      const result = await createUserAndSubscribe.mutateAsync({
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        planIdentifier: selectedPlan.plan_identifier,
        // Pass promo code if available (from URL)
        ...(promoCode ? { promoCode } : {}),
        // Trial length is derived server-side from the plan row; the edge
        // function ignores any client-supplied value.
      });

      // Check result - if payment is required and checkout URL exists,
      // redirect already happened in hook's onSuccess, don't do anything
      if ('success' in result && result.success) {
        // If paid plan with checkout URL - user is already being redirected
        if (result.plan.requires_payment && result.checkout?.url) {
          console.log('User should be redirected to Stripe checkout');
          return; // Don't proceed to success step
        }
        
        // If redirect_url exists - user is also already being redirected
        if (result.redirect_url) {
          console.log('User should be redirected to:', result.redirect_url);
          return; // Don't proceed to success step
        }
      }
      
      // Only for free plans without redirect_url show success step
      setStep('success');
      reset();
      
      // Redirect after 3 seconds
      redirectTimerRef.current = setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      console.error('Sign up with plan error:', errorMessage);
      setApiError(errorMessage);
    }
  };

  if (plansLoading || authLoading || billingLoading || statusLoading || brandingLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load subscription plans. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={branding.companyName}
              className="mx-auto h-12 w-auto mb-4"
            />
          )}
          <h1 className="text-4xl font-bold text-foreground mb-2">Welcome to {branding.companyName}</h1>
          <p className="text-lg text-muted-foreground">
            Choose your plan and create your account
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 space-x-4">
          <div className={`flex items-center space-x-2 ${step === 'select-plan' ? 'text-primary' : 'text-green-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'select-plan' ? 'bg-primary text-primary-foreground' : 'bg-green-600 text-white'}`}>
              {step !== 'select-plan' ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <span className="font-medium">Select Plan</span>
          </div>
          
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          
          <div className={`flex items-center space-x-2 ${step === 'register' ? 'text-primary' : step === 'success' ? 'text-green-600' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'register' ? 'bg-primary text-primary-foreground' : step === 'success' ? 'bg-green-600 text-white' : 'bg-muted-foreground text-white'}`}>
              {step === 'success' ? <Check className="w-4 h-4" /> : '2'}
            </div>
            <span className="font-medium">Create Account</span>
          </div>
          
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          
          <div className={`flex items-center space-x-2 ${step === 'success' ? 'text-green-600' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'success' ? 'bg-green-600 text-white' : 'bg-muted-foreground text-white'}`}>
              {step === 'success' ? <Check className="w-4 h-4" /> : '3'}
            </div>
            <span className="font-medium">Complete</span>
          </div>
        </div>

        {/* Step Content */}
        {step === 'select-plan' && (
          <div>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold mb-2">Choose Your Plan</h2>
              <p className="text-muted-foreground">Select the plan that best fits your business needs</p>
            </div>
            
            <div className={`grid gap-6 ${
              visiblePlans.length === 1 
                ? 'grid-cols-1 max-w-md mx-auto' 
                : visiblePlans.length === 2
                ? 'md:grid-cols-2 max-w-4xl mx-auto'
                : 'md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {visiblePlans.map((plan: SubscriptionPlan, planIdx: number) => (
                <Card
                  key={plan.id}
                  className="relative transition-all duration-200 hover:shadow-lg cursor-pointer"
                  onClick={() => handlePlanSelect(plan)}
                >
                  {planIdx === highlightIdx && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground shadow-md">
                        <Crown className="w-3 h-3 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  
                  {plan.has_trial && plan.trial_period_days && (
                    <div className="absolute -top-3 right-4">
                      <Badge className="bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg animate-pulse">
                        <Zap className="w-3 h-3 mr-1" />
                        {plan.trial_period_days} Days FREE!
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader>
                    <CardTitle className="truncate" title={plan.name}>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="mb-4">
                      {plan.price_one_time > 0 && plan.price_monthly > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {discountPercent > 0 && (
                              <div className="text-lg text-muted-foreground line-through">
                                ${plan.price_one_time}
                              </div>
                            )}
                            <div className="text-2xl font-bold text-foreground">
                              ${calculateDiscountedPrice(plan.price_one_time)}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">one-time setup</div>
                          <div className="flex items-center gap-2">
                            {discountPercent > 0 && (
                              <div className="text-sm text-muted-foreground line-through">
                                ${plan.price_monthly}
                              </div>
                            )}
                            <div className="text-lg font-semibold text-primary">
                              + ${calculateDiscountedPrice(plan.price_monthly)}/month
                            </div>
                          </div>
                          {plan.has_trial && plan.trial_period_days && (
                            <div className="mt-2 p-2 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                              <div className="flex items-center justify-center gap-2">
                                <Zap className="w-4 h-4 text-green-500" />
                                <span className="text-sm font-semibold text-green-700">
                                  Try FREE for {plan.trial_period_days} days!
                                </span>
                              </div>
                              <div className="text-xs text-center text-green-600 mt-1">
                                Includes {getTrialCreditCap(plan)} trial credits &middot; Card required &middot;
                                ${calculateDiscountedPrice(plan.price_monthly)}/mo after trial &middot; Cancel anytime
                              </div>
                            </div>
                          )}
                          {discountPercent > 0 && (
                            <div className="text-xs text-green-600 font-medium">
                              🎉 {discountPercent}% off applied!
                            </div>
                          )}
                        </div>
                      ) : plan.price_one_time > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {discountPercent > 0 && (
                              <div className="text-2xl text-muted-foreground line-through">
                                ${plan.price_one_time}
                              </div>
                            )}
                            <div className="text-3xl font-bold text-primary">
                              ${calculateDiscountedPrice(plan.price_one_time)}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">one-time payment</div>
                          <div className="flex items-center gap-1">
                            <div className="text-xs text-green-600 font-medium">Lifetime access • No monthly fees</div>
                            {discountPercent > 0 && (
                              <div className="text-xs text-green-600 font-medium">• {discountPercent}% off!</div>
                            )}
                          </div>
                        </div>
                      ) : plan.price_monthly > 0 ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {discountPercent > 0 && (
                              <div className="text-2xl text-muted-foreground line-through">
                                ${plan.price_monthly}
                              </div>
                            )}
                            <div className="text-3xl font-bold text-foreground">
                              ${calculateDiscountedPrice(plan.price_monthly)}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">per month</div>
                          {plan.has_trial && plan.trial_period_days && (
                            <div className="mt-2 p-2 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                              <div className="flex items-center justify-center gap-2">
                                <Zap className="w-4 h-4 text-green-500" />
                                <span className="text-sm font-semibold text-green-700">
                                  Try FREE for {plan.trial_period_days} days!
                                </span>
                              </div>
                              <div className="text-xs text-center text-green-600 mt-1">
                                Includes {getTrialCreditCap(plan)} trial credits &middot; Card required &middot;
                                ${calculateDiscountedPrice(plan.price_monthly)}/mo after trial &middot; Cancel anytime
                              </div>
                            </div>
                          )}
                          {discountPercent > 0 && (
                            <div className="text-xs text-green-600 font-medium">
                              🎉 {discountPercent}% off applied!
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="text-3xl font-bold text-green-600">
                            Free
                          </div>
                          <div className="text-sm text-green-600 font-medium">no payment required</div>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {+plan.monthly_credits > 0 && (
                        <div className="flex items-center text-sm">
                          <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                          {plan.monthly_credits.toLocaleString()} monthly credits
                        </div>
                      )}
                      {plan.one_time_bonus_credits > 0 && (
                        <div className="flex items-center text-sm">
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          {plan.one_time_bonus_credits.toLocaleString()} bonus credits
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      {plan.features.map((feature: string, index: number) => (
                        <div key={index} className="flex items-start text-sm">
                          <Check className="w-4 h-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  
                  <CardFooter>
                    <Button className="w-full">
                      Choose {plan.name}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        )}

        {step === 'register' && selectedPlan && (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold mb-2">Create Your Account</h2>
              <div className="bg-background p-4 rounded-lg border">
                <p className="text-sm text-muted-foreground mb-1">Selected plan:</p>
                <p className="font-semibold text-primary">{selectedPlan.name}</p>
                {selectedPlan.price_monthly > 0 || selectedPlan.price_one_time > 0 ? (
                  <div className="space-y-1">
                    {discountPercent > 0 && (
                      <p className="text-xs text-green-600 font-medium">
                        🎉 {discountPercent}% discount applied
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {selectedPlan.price_one_time > 0 && selectedPlan.price_monthly > 0
                        ? discountPercent > 0
                          ? `$${calculateDiscountedPrice(selectedPlan.price_one_time)} setup + $${calculateDiscountedPrice(selectedPlan.price_monthly)}/month (was $${selectedPlan.price_one_time} + $${selectedPlan.price_monthly}/month)`
                          : `$${selectedPlan.price_one_time} setup + $${selectedPlan.price_monthly}/month`
                        : selectedPlan.price_one_time > 0
                        ? discountPercent > 0
                          ? `$${calculateDiscountedPrice(selectedPlan.price_one_time)} one-time (was $${selectedPlan.price_one_time})`
                          : `$${selectedPlan.price_one_time} one-time`
                        : discountPercent > 0
                        ? `$${calculateDiscountedPrice(selectedPlan.price_monthly)}/month (was $${selectedPlan.price_monthly}/month)`
                        : `$${selectedPlan.price_monthly}/month`
                      }
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-green-600 font-medium">Free plan</p>
                )}
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg shadow-md">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {apiError && (
                  <Alert className="border-destructive/20 bg-destructive/5">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-destructive">
                      {apiError}
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    {...register('fullName')}
                    className={errors.fullName ? 'border-destructive' : ''}
                    disabled={isSubmitting}
                    placeholder="John Doe"
                  />
                  {errors.fullName && (
                    <p className="text-xs text-destructive mt-1">{errors.fullName.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    className={errors.email ? 'border-destructive' : ''}
                    disabled={isSubmitting}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    {...register('password')}
                    className={errors.password ? 'border-destructive' : ''}
                    disabled={isSubmitting}
                    placeholder="••••••••"
                  />
                  {errors.password && (
                    <p className="text-xs text-destructive mt-1">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    {...register('confirmPassword')}
                    className={errors.confirmPassword ? 'border-destructive' : ''}
                    disabled={isSubmitting}
                    placeholder="••••••••"
                  />
                  {errors.confirmPassword && (
                    <p className="text-xs text-destructive mt-1">{errors.confirmPassword.message}</p>
                  )}
                </div>


                {selectedPlan.requires_payment && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      After account creation, you'll be redirected to add a payment method to activate your subscription.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Controller
                      name="smsConsent"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id="smsConsent"
                          checked={field.value === true}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                          disabled={isSubmitting}
                          className="mt-1"
                        />
                      )}
                    />
                    <Label htmlFor="smsConsent" className="text-xs leading-snug font-normal text-muted-foreground cursor-pointer">
                      <span className="font-medium text-foreground">Optional:</span> Yes, send me transactional
                      SMS notifications from {branding.companyName} about my account, billing, system alerts,
                      and customer-care communications. You can use {branding.companyName} without checking
                      this box. Msg & data rates may apply. Message frequency varies. Reply HELP for help
                      or STOP to unsubscribe at any time. See our{' '}
                      <a href={legalUrls.privacyPolicy} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href={legalUrls.termsOfService} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                        Terms of Service
                      </a>.
                    </Label>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('select-plan')}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Back to Plans
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </form>

              <div className="mt-4 text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary hover:underline"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        )}

        {step === 'success' && selectedPlan && (
          <div className="max-w-md mx-auto text-center">
            <div className="bg-background p-8 rounded-lg shadow-md">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <h2 className="text-2xl font-semibold mb-2 text-green-600">Account Created!</h2>
              <p className="text-muted-foreground mb-4">
                Your account has been successfully created with the <strong>{selectedPlan.name}</strong> plan.
              </p>
              
              {/* Show checkout fallback button if plan requires payment */}
              {selectedPlan.requires_payment && createUserAndSubscribe.data &&
                'success' in createUserAndSubscribe.data &&
                createUserAndSubscribe.data.success &&
                createUserAndSubscribe.data.checkout?.url ? (
                <div className="mb-4">
                  <Alert className="mb-4 border-blue-200 bg-blue-50">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      Complete your subscription activation by adding a payment method.
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={() => {
                      const data = createUserAndSubscribe.data;
                      if (data && 'success' in data && data.success && data.checkout?.url) {
                        window.location.href = data.checkout.url;
                      }
                    }}
                    className="w-full mb-2"
                  >
                    Continue to Payment
                  </Button>
                </div>
              ) : selectedPlan.requires_payment ? (
                <Alert className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please check your email for a verification link, then sign in to add your payment method and activate your subscription.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="mb-4 border-green-200 bg-green-50">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Your free plan is ready to use! Please check your email for a verification link.
                  </AlertDescription>
                </Alert>
              )}
              
              <p className="text-sm text-muted-foreground mb-4">
                Redirecting you to the login page in a few seconds...
              </p>
              
              <Button
                variant={selectedPlan.requires_payment && createUserAndSubscribe.data &&
                  'success' in createUserAndSubscribe.data &&
                  createUserAndSubscribe.data.success &&
                  createUserAndSubscribe.data.checkout?.url ? "outline" : "default"}
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Go to Login
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-muted-foreground">
          <a href={legalUrls.privacyPolicy} target="_blank" rel="noopener noreferrer" className="hover:underline">
            Privacy Policy
          </a>
          <span className="mx-2">|</span>
          <a href={legalUrls.termsOfService} target="_blank" rel="noopener noreferrer" className="hover:underline">
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
}